// @ts-strict-ignore
import { useMutation } from "@apollo/client";
import useNotifier from "@dashboard/hooks/useNotifier";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@saleor/macaw-ui";
import React, { useState } from "react";

import { WALLET_STAFF_ADJUSTMENT_MUTATION } from "../../walletMutations";
import { CUSTOMER_WALLET_QUERY } from "../../walletQueries";

const useStyles = makeStyles(
  (theme: any) => ({
    root: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2.5),
      paddingTop: theme.spacing(1),
    },
    row: {
      display: "flex",
      gap: theme.spacing(2),
    },
    radioGroup: {
      flexDirection: "row",
      gap: theme.spacing(2),
    },
    helpText: {
      color: theme.palette.text.secondary,
      fontSize: "0.85rem",
    },
  }),
  { name: "WalletStaffAdjustmentDialog" },
);

interface WalletStaffAdjustmentDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  customerName?: string;
  onSuccess?: () => void;
}

export const WalletStaffAdjustmentDialog: React.FC<WalletStaffAdjustmentDialogProps> = ({
  open,
  onClose,
  userId,
  customerName,
  onSuccess,
}) => {
  const classes = useStyles();
  const notify = useNotifier();

  const [entryType, setEntryType] = useState<"credit" | "debit">("credit");
  const [balanceSource, setBalanceSource] = useState<"real" | "promotional">("promotional");
  const [amount, setAmount] = useState<string>("");
  const [reason, setReason] = useState<string>("");

  const [adjustWallet, { loading }] = useMutation(WALLET_STAFF_ADJUSTMENT_MUTATION, {
    refetchQueries: [{ query: CUSTOMER_WALLET_QUERY, variables: { userId } }],
  });

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const numAmount = parseFloat(amount);

    if (isNaN(numAmount) || numAmount <= 0) {
      notify({
        status: "error",
        text: "Please enter a valid positive adjustment amount.",
      });

      return;
    }

    if (!reason.trim()) {
      notify({
        status: "error",
        text: "Audit reason is strictly mandatory for manual balance adjustments.",
      });

      return;
    }

    try {
      const response = await adjustWallet({
        variables: {
          userId,
          amount: numAmount,
          balanceSource,
          entryType,
          reason: reason.trim(),
        },
      });

      if (response.errors && response.errors.length > 0) {
        notify({
          status: "error",
          text: response.errors.map(err => err.message).join(", "),
        });

        return;
      }

      if (!response.data?.walletStaffAdjustment) {
        notify({
          status: "error",
          text: "Failed to adjust balance: Action not permitted or request failed.",
        });

        return;
      }

      const errors = response.data.walletStaffAdjustment.errors;

      if (errors && errors.length > 0) {
        notify({
          status: "error",
          text: errors.map(err => err.message).join(", "),
        });

        return;
      }

      notify({
        status: "success",
        text: `Successfully ${entryType === "credit" ? "credited" : "debited"} ₹${numAmount.toFixed(
          2,
        )} to ${balanceSource} balance.`,
      });

      setAmount("");
      setReason("");
      onClose();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      notify({
        status: "error",
        text: err.message || "Failed to adjust customer wallet balance.",
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <form
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }}
      >
        <DialogTitle>
          Adjust Customer Wallet Balance
          {customerName && (
            <Typography variant="body2" color="textSecondary">
              For: {customerName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers className={classes.root}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Action Type</FormLabel>
            <RadioGroup
              row
              value={entryType}
              onChange={e => setEntryType(e.target.value as "credit" | "debit")}
              className={classes.radioGroup}
            >
              <FormControlLabel
                value="credit"
                control={<Radio color="primary" />}
                label="Credit (+)"
              />
              <FormControlLabel
                value="debit"
                control={<Radio color="primary" />}
                label="Debit (-)"
              />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset">
            <FormLabel component="legend">Balance Target</FormLabel>
            <RadioGroup
              row
              value={balanceSource}
              onChange={e => setBalanceSource(e.target.value as "real" | "promotional")}
              className={classes.radioGroup}
            >
              <FormControlLabel
                value="promotional"
                control={<Radio color="primary" />}
                label="Promotional / Bonus"
              />
              <FormControlLabel
                value="real"
                control={<Radio color="primary" />}
                label="Real Money"
              />
            </RadioGroup>
          </FormControl>

          <TextField
            label="Adjustment Amount (₹)"
            type="number"
            inputProps={{ min: "0.01", step: "0.01" }}
            value={amount}
            onChange={e => setAmount(e.target.value)}
            fullWidth
            required
            variant="outlined"
            placeholder="e.g. 100.00"
          />

          <TextField
            label="Mandatory Audit Reason"
            multiline
            rows={3}
            value={reason}
            onChange={e => setReason(e.target.value)}
            fullWidth
            required
            variant="outlined"
            placeholder="Explain why this adjustment is being made (e.g. Customer goodwill compensation for order #405, VIP welcome credit, etc.)."
            helperText="This reason is permanently recorded in the immutable audit ledger."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? "Adjusting..." : "Confirm Adjustment"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default WalletStaffAdjustmentDialog;
